import { useMemo } from 'react';
import { Flex, Modal, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useSecurityTypeTranslation } from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController.ts';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { InvestorHoldingRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/investorHoldings.types.ts';
import { getTransactionTableColumns } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/TransactionTable/transactionTableColumns.tsx';
import { getTransactionVestingTermAccess } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/TransactionTable/transactionTable.utils.ts';
import { TransactionVestingTermForm } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingTransactionsModal/TransactionVestingTermForm.tsx';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { useInvestmentReference } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/TransactionTable/useInvestmentReference.ts';
import {
	getHoldingTransactionsSubtitle,
	getHoldingTransactionTotals,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingTransactionsModal/holdingTransactions.utils.ts';
import { InvestorDetailsEmptyCardState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsEmptyCardState/InvestorDetailsEmptyCardState.tsx';
import { holdingTransactionsModalStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingTransactionsModal/HoldingTransactionsModal.styles.tsx';
import { useHoldingTransactions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingTransactionsModal/useHoldingTransactions.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';

interface Props {
	investorId: string;
	holding: InvestorHoldingRow | null;
	onClose: () => void;
}

export const HoldingTransactionsModal = ({ investorId, holding, onClose }: Props) => {
	const tMipManage = useTranslationMipManage();
	const getSecurityTypeTranslation = useSecurityTypeTranslation();
	const getInvestmentReference = useInvestmentReference();
	const dataOperations = useMantineTableDataOperations();
	const { mipId } = useRequiredParams('mipId');
	const { vestingTerms, refetchVestingTerms } = useMIPManageContext();
	const { hasPermission: canEditVesting } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: mipId,
		permissionRole: PermissionRole.EDITOR,
	});

	const {
		rows: transactions,
		isLoading,
		isError,
	} = useHoldingTransactions({ investorId, holding });

	const columns = useMemo(
		() =>
			getTransactionTableColumns({
				tMipManage,
				currency: holding?.currency,
				vestingTerms,
				mipId,
				view: 'completed',
				getInvestmentReference,
				getSecurityTypeTranslation,
				expandableVestingTerm: canEditVesting,
				onVestingTermOpen: () => void refetchVestingTerms(),
				totals: holding ? getHoldingTransactionTotals(holding, transactions) : undefined,
			}),
		[
			tMipManage,
			holding?.currency,
			vestingTerms,
			mipId,
			getInvestmentReference,
			getSecurityTypeTranslation,
			transactions,
			canEditVesting,
		]
	);

	return (
		<Modal
			opened={!!holding}
			onClose={onClose}
			size="75rem"
			centered
			data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_TRANSACTIONS_MODAL}
			title={
				holding && (
					<Flex direction="column">
						<Text mb="0.25rem" fz={18} fw={600} c="dark.8">
							{tMipManage('investors.details.holding_transactions.title', {
								issuerName: holding.issuerName,
							})}
						</Text>

						<Text fz={12} c="dark.8">
							{getHoldingTransactionsSubtitle({
								holding,
								tMipManage,
								securityTypeLabel: getSecurityTypeTranslation(holding.securityType),
							})}
						</Text>
					</Flex>
				)
			}
			styles={holdingTransactionsModalStyles}
		>
			{isError ? (
				<InvestorDetailsEmptyCardState
					icon="es-pie-chart"
					title={tMipManage('investors.details.holding_transactions.error_title')}
					dataTestId={
						TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_TRANSACTIONS_ERROR
					}
				/>
			) : (
				<Flex direction="column">
					<MantineTable
						data={transactions}
						columns={columns}
						getRowId={(row) => `${row.id}-${row.transactionType}`}
						dataOperations={dataOperations}
						manualSorting={false}
						enablePagination={false}
						enableColumnResizing
						enableSearchBar={false}
						enableTopToolbar={false}
						enableRowSelection={false}
						enableFilters={false}
						enableHiding={false}
						enableStickyFooter={false}
						isLoading={isLoading}
						enableExpanding={canEditVesting}
						getRowCanExpand={(row) =>
							!!getTransactionVestingTermAccess({
								transaction: row.original,
								hasVestingTerms: !!vestingTerms?.length,
								view: 'completed',
							})
						}
						renderDetailPanel={({ row, table }) => (
							<TransactionVestingTermForm
								transaction={row.original}
								vestingTerms={vestingTerms ?? []}
								onClose={() => table.resetExpanded()}
							/>
						)}
						mantineTableBodyRowProps={() => ({ style: { height: 'unset' } })}
					/>

					<Flex align="center" gap="0.25rem" mt="1rem">
						<Icon variant="information" size="vlg" />

						<Text fz={12} c="dark.8">
							{tMipManage('investors.details.holding_transactions.info')}
						</Text>
					</Flex>
				</Flex>
			)}
		</Modal>
	);
};
